import React, { useEffect, useState, useCallback, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import Strike from "@tiptap/extension-strike";
import Link from "@tiptap/extension-link";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import "./PolicyTipTapEditor.css";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  RotateCcw,
  RotateCw,
  Underline as UnderlineIcon,
  Strikethrough,
  Link as LinkIcon,
  Type,
} from "lucide-react";
import PolicyStudioActionButton from "./PolicyStudioActionButton";

// Custom extension to highlight text in square brackets
const squareBracketHighlightKey = new PluginKey("square-bracket-highlight");

const SquareBracketHighlight = Extension.create({
  name: "squareBracketHighlight",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: squareBracketHighlightKey,
        props: {
          decorations(state) {
            const decorations: Decoration[] = [];
            const regex = /\[(.*?)\]/g;

            state.doc.descendants((node, pos) => {
              if (node.isText) {
                const text = node.text || "";
                let match;

                while ((match = regex.exec(text)) !== null) {
                  const start = pos + match.index;
                  const end = start + match[0].length;

                  decorations.push(
                    Decoration.inline(start, end, {
                      style: "color: blue;",
                    })
                  );
                }
              }
            });

            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});

interface PolicyTipTapEditorProps {
  content: string;
  editable: boolean;
  isLoading?: boolean;
  onChange?: (html: string) => void;
  onSave?: () => void;
  onToggleEdit?: () => void;
  onCancel?: () => void;
}

export const PolicyTipTapEditor = ({
  content,
  editable,
  isLoading = false,
  onChange,
  onSave,
  onToggleEdit,
  onCancel,
}: PolicyTipTapEditorProps) => {
  const [linkUrl, setLinkUrl] = useState<string>("");
  const [showLinkInput, setShowLinkInput] = useState<boolean>(false);
  const [originalContent, setOriginalContent] = useState<string>(content);
  const [currentEditorContent, setCurrentEditorContent] = useState<string>("");
  const linkInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: false,
        orderedList: false,
        listItem: false,
      }),
      BulletList.configure({
        HTMLAttributes: {
          class: "policy-tiptap-editor-bullet-list",
        },
      }),
      OrderedList.configure({
        HTMLAttributes: {
          class: "policy-tiptap-editor-ordered-list",
        },
      }),
      ListItem,
      Placeholder.configure({
        placeholder: "Start writing your policy here...",
      }),
      Underline,
      Strike,
      Link.configure({
        openOnClick: true,
        linkOnPaste: true,
      }),
      SquareBracketHighlight,
    ],
    content,
    editable,
    onUpdate: ({ editor }) => {
      // Store the current content
      setCurrentEditorContent(editor.getHTML());
      onChange?.(editor.getHTML());
    },
    autofocus: editable,
  });

  // Create a helper function to ensure the editor is focused before running commands
  const focusEditor = useCallback(() => {
    if (editor && !editor.isFocused) {
      editor.commands.focus();
    }
  }, [editor]);

  useEffect(() => {
    if (editor && editor.isEditable !== editable) {
      editor.setEditable(editable);
      if (editable) {
        // Auto-focus when becoming editable
        //editor.commands.focus();
      }
    }
  }, [editor, editable]);

  useEffect(() => {
    if (editor && content && editor.getHTML() !== content) {
      editor.commands.setContent(content);
    }
  }, [editor, content]);

  // Store the original content when entering edit mode
  useEffect(() => {
    if (editable) {
      setOriginalContent(content);
    }
  }, [editable, content]);

  // Focus the link input when it appears
  useEffect(() => {
    if (showLinkInput && linkInputRef.current) {
      // Small delay to ensure the input is rendered before focusing
      setTimeout(() => {
        linkInputRef.current?.focus();
      }, 10);
    }
  }, [showLinkInput]);

  const handleSave = useCallback(() => {
    // Ensure we save the most recent content
    if (onSave) {
      // Using the stored content from state to avoid focus issues
      onSave();
    }
  }, [onSave]);

  const handleCancel = () => {
    if (editor) {
      // Reset content to the original content
      editor.commands.setContent(originalContent);
    }
    // Call the parent's onCancel handler to exit edit mode
    onCancel?.();
  };

  const setLink = () => {
    if (!editor) return;

    if (linkUrl === "") {
      editor.chain().extendMarkRange("link").unsetLink().run();
      return;
    }

    // Add https:// if no protocol is specified
    const url =
      linkUrl.startsWith("http://") || linkUrl.startsWith("https://")
        ? linkUrl
        : `https://${linkUrl}`;

    editor.chain().extendMarkRange("link").setLink({ href: url }).run();

    setLinkUrl("");
    setShowLinkInput(false);

    // Return focus to editor after adding link
    //focusEditor();
  };

  const EditModeMenuBar = () => {
    if (!editor) {
      return null;
    }

    return (
      <div className="tiptap-menu-bar">
        <div className="tiptap-menu-left">
          <button
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent losing focus
              focusEditor();
              editor.chain().undo().run();
            }}
            aria-label="Undo">
            <RotateCcw size={16} />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().redo().run();
            }}
            aria-label="Redo">
            <RotateCw size={16} />
          </button>
          <div className="tiptap-divider" />
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleBold().run();
            }}
            className={editor.isActive("bold") ? "is-active" : ""}
            aria-label="Bold">
            <Bold size={16} />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleItalic().run();
            }}
            className={editor.isActive("italic") ? "is-active" : ""}
            aria-label="Italic">
            <Italic size={16} />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleUnderline().run();
            }}
            className={editor.isActive("underline") ? "is-active" : ""}
            aria-label="Underline">
            <UnderlineIcon size={16} />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleStrike().run();
            }}
            className={editor.isActive("strike") ? "is-active" : ""}
            aria-label="Strikethrough">
            <Strikethrough size={16} />
          </button>
          <div className="tiptap-divider" />
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().unsetAllMarks().clearNodes().run();
            }}
            aria-label="Clear formatting">
            <Type size={16} />
          </button>
          <div className="tiptap-divider" />
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleHeading({ level: 1 }).run();
            }}
            className={
              editor.isActive("heading", { level: 1 }) ? "is-active" : ""
            }
            aria-label="Heading 1">
            <Heading1 size={16} />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleHeading({ level: 2 }).run();
            }}
            className={
              editor.isActive("heading", { level: 2 }) ? "is-active" : ""
            }
            aria-label="Heading 2">
            <Heading2 size={16} />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleHeading({ level: 3 }).run();
            }}
            className={
              editor.isActive("heading", { level: 3 }) ? "is-active" : ""
            }
            aria-label="Heading 3">
            <Heading3 size={16} />
          </button>
          <div className="tiptap-divider" />
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleBulletList().run();
            }}
            className={editor.isActive("bulletList") ? "is-active" : ""}
            aria-label="Bullet List">
            <List size={16} />
          </button>
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleOrderedList().run();
            }}
            className={editor.isActive("orderedList") ? "is-active" : ""}
            aria-label="Ordered List">
            <ListOrdered size={16} />
          </button>
          <div className="tiptap-divider" />
          <button
            onMouseDown={(e) => {
              e.preventDefault();
              focusEditor();
              editor.chain().toggleBlockquote().run();
            }}
            className={editor.isActive("blockquote") ? "is-active" : ""}
            aria-label="Blockquote">
            <Quote size={16} />
          </button>
          {/*<div className="tiptap-divider" />
           <button
            onMouseDown={(e) => {
              e.preventDefault();
              if (showLinkInput) {
                setShowLinkInput(false);
                focusEditor();
              } else {
                setShowLinkInput(true);
                // Don't focus editor immediately when opening link input
              }
            }}
            className={editor.isActive("link") ? "is-active" : ""}
            aria-label="Link">
            <LinkIcon size={16} />
          </button> */}
          {showLinkInput && (
            <div className="tiptap-link-input">
              <input
                ref={linkInputRef}
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="Enter URL"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setLink();
                  } else if (e.key === "Escape") {
                    setShowLinkInput(false);
                    setLinkUrl("");
                    focusEditor();
                  }
                }}
                onBlur={(e) => {
                  // Prevent immediate closing if clicked inside input
                  if (
                    e.relatedTarget &&
                    e.relatedTarget.closest(".tiptap-link-input")
                  ) {
                    return;
                  }
                }}
              />
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  setLink();
                }}>
                Add
              </button>
            </div>
          )}
        </div>
        <div className="tiptap-menu-right">
          <PolicyStudioActionButton
            label="Cancel"
            variant="secondary"
            onClick={handleCancel}
          />
          <PolicyStudioActionButton
            label="Save"
            variant="primary"
            onMouseDown={(e) => {
              e.preventDefault(); // Prevent focus loss
              onSave?.();
            }}
          />
        </div>
      </div>
    );
  };

  const ViewModeMenuBar = () => {
    return (
      <div className="tiptap-menu-bar">
        <div className="tiptap-menu-left"></div>
        <div className="tiptap-menu-right">
          <PolicyStudioActionButton
            label="Edit"
            variant="secondary"
            onClick={onToggleEdit}
            disabled={isLoading}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={`policy-tiptap-editor ${editable ? "is-editable" : ""}`}>
      {editable ? <EditModeMenuBar /> : <ViewModeMenuBar />}
      <EditorContent editor={editor} />
    </div>
  );
};
